import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { classNames } from "../helpers/classNames";
import useScrollState from "../hooks/useScrollState";
import Image from "../components/elements/Image";
import Logo from "../assets/images/logo.png";
import Button from "../components/form/Button";
import useScrollSpy from "../hooks/useScrollSpy";

const FrontHeader = () => {
  const isScrolled = useScrollState();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const menudata = [
    { link: "#features", labelKey: "Features" },
    { link: "#coins", labelKey: "Coins" },
    { link: "#artist", labelKey: "Artist" },
    { link: "#preview", labelKey: "Preview" },
    { link: "#faq", labelKey: "Faq" },
    { link: "#contact", labelKey: "Contact" },
  ];
  
  const sectionIds = menudata.map((item) => item.link);
  const activeSection = useScrollSpy(sectionIds, 100);
  
  const appStoreData = [
    { labelKey: "Google Play", link: "/", icon: "fa-brands fa-google-play" },
    {
      labelKey: "Apple Store",
      link: "/",
      icon: "fa-brands fa-apple",
    },
  ];

  const handleLogoClick = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname === "/") {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      navigate("/");
    }
  };

  const handleSectionClick = (e, sectionId) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: sectionId.replace("#", "") } });
    } else {
      const el = document.querySelector(sectionId);
      if (el) {
        window.scrollTo({
          top: el.offsetTop - 96,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <>
      <header
        className={classNames(
          "fixed left-1/2 -translate-x-1/2 w-full h-16 sm:h-20 xls:h-24 flex items-center z-10 bg-woodsmoke transition-all duration-300 before:content-[''] before:absolute before:inset-0 before:z-[-1] before:bg-gradient-to-r before:from-electric-violet/50 before:via-transparent before:to-amber-500/50 before:from-10% before:to-90% before:transition-all before:duration-300",
          isScrolled
            ? "top-0 max-w-full before:opacity-50 bg-[#0b0b0f]"
            : "sm:top-6  sm:rounded-xl before:opacity-10 sm:before:opacity-5"
        )}
      >
        <div className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl xls:max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl mx-auto px-3 sm:px-4">
          <div className="flex items-center gap-x-4 sm:gap-x-6 lg:gap-x-10">
            {/* Logo */}
            <div className="relative h-12 sm:h-16 md:h-20  flex overflow-hidden flex-shrink-0">
              <Link
                to={"/"}
                onClick={handleLogoClick}
                className="w-full h-full overflow-hidden flex"
              >
                <Image
                  src={Logo}
                  alt="Logo"
                  effect="blur"
                  classes="object-contain object-left"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="relative ml-auto hidden xls:flex items-center justify-center gap-4 xl:gap-6 2xl:gap-8">
              {menudata.map((item, index) => (
                <div
                  key={index}
                  className="relative"
                >
                  <a
                    href={item.link}
                    onClick={(e) => handleSectionClick(e, item.link)}
                    className={classNames(
                      "flex-shrink flex-grow flex relative items-center justify-center py-2 transition-all duration-300 font-semibold text-sm lg:text-base whitespace-nowrap hover:-translate-y-0.5",
                      activeSection === item.link
                        ? "text-amber-500"
                        : "text-white"
                    )}
                  >
                    <span>{item.labelKey}</span>

                    {activeSection === item.link && (
                      <span
                        className="absolute bottom-0 left-0 w-full h-0.5 bg-amber-500 rounded-full"
                      />
                    )}
                  </a>
                </div>
              ))}
            </div>

            {/* Desktop App Store Buttons */}
            <div className="hidden xls:flex items-center justify-end gap-3 flex-shrink-0">
              {appStoreData.map((item, index) => (
                <a
                  key={index}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 lg:px-4 h-10 lg:h-12 rounded-full font-black transition-all flex items-center justify-center gap-2 lg:gap-3 shadow-xl bg-gradient-to-r from-yellow-400 to-orange-500 text-[#320051] hover:from-yellow-300 hover:to-orange-400"
                >
                  <i className={classNames("fa-fw text-base lg:text-lg", item.icon)}></i>
                  <span className="text-xs lg:text-sm font-medium whitespace-nowrap">
                    {item.labelKey}
                  </span>
                </a>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <div className="ml-auto flex xls:hidden flex-shrink-0">
              <Button
                buttonIcon={"fa-duotone fa-solid fa-bars-staggered"}
                buttonIconPosition="left"
                buttonClasses={
                  "after:hidden !bg-none !px-0 aspect-square !text-2xl sm:!text-3xl"
                }
                buttonFunction={() => setMenuOpen(!menuOpen)}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Menu */}
      <aside
        className={classNames(
          "fixed top-0 left-0 bottom-0 z-20 bg-woodsmoke overflow-hidden w-full max-w-[280px] sm:max-w-xs flex flex-col xls:hidden transition-all duration-300 bg-medium-purple/90",
          menuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="absolute inset-0 z-[-1] bg-gradient-to-tr from-electric-violet/20 to-radical-red/20" />
        
        {/* Mobile Logo */}
        <div className="flex items-center px-4 sm:px-6 h-16 sm:h-20 relative flex-shrink-0">
          <div className="relative h-10 sm:h-12 flex overflow-hidden">
            <Link
              to={"/"}
              onClick={handleLogoClick}
              className="w-full h-full overflow-hidden flex"
            >
              <Image
                src={Logo}
                alt="Logo"
                effect="blur"
                classes="object-contain object-left"
              />
            </Link>
          </div>
        </div>

        {/* Mobile Navigation Links */}
        <div className="flex flex-col justify-start items-start px-4 sm:px-6 py-4 flex-1 overflow-y-auto">
          {menudata.map((item, index) => (
            <div 
              key={index} 
              className="relative w-full group"
            >
              <a
                href={item.link}
                onClick={(e) => handleSectionClick(e, item.link)}
                className={classNames(
                  "flex-shrink flex-grow flex relative items-center py-3 transition-all duration-300 font-semibold text-base w-full group-hover:translate-x-1",
                  activeSection === item.link
                    ? "text-yellow-400"
                    : "text-white"
                )}
              >
                <span>{item.labelKey}</span>

                {activeSection === item.link && (
                  <span
                    className="absolute bottom-0 left-0 w-full h-0.5 bg-yellow-400 rounded-full"
                  />
                )}
              </a>
            </div>
          ))}
        </div>

        {/* Mobile App Store Buttons */}
        <div className="h-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 px-4 sm:px-6 py-4 mt-auto flex-shrink-0">
          {appStoreData.map((item, index) => (
            <a
              key={index}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-3 sm:py-3.5 rounded-full font-black text-lg transition-all flex items-center justify-center gap-2 sm:gap-3 shadow-xl bg-gradient-to-r from-yellow-400 to-orange-500 text-[#320051] hover:from-yellow-300 hover:to-orange-400 flex-1"
            >
              <i className={classNames("fa-fw text-lg", item.icon)}></i>
              <span className="text-xs font-semibold sm:text-sm whitespace-nowrap">
                {item.labelKey}
              </span>
            </a>
          ))}
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      <button
        type="button"
        onClick={() => setMenuOpen(!menuOpen)}
        className={classNames(
          "fixed inset-0 z-[19] bg-woodsmoke transition-all duration-300 xls:hidden",
          menuOpen ? "opacity-80 visible" : "opacity-0 invisible"
        )}
      />
    </>
  );
};

export default FrontHeader;